// @ts-nocheck
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { getDataViewKey, setDataViewLoaded, setDataViewLoading } from "@insite/client-framework/Store/Data/DataState";
import objectKeys from "@store/Framework/Common/Utilities/objectKeys";
import {
    getTrailerPickupRequestKey,
    GetTrailerTrackingsV2ApiParameter,
} from "@store/Framework/Services/TrailerServiceV2";
import TrailerTrackingV2State, {
    TrailerTrackingV2DataView,
} from "@store/Framework/Store/Greif/Pages/TrailerTrackingV2/TrailerTrackingV2State";
import { TrailerPickupRequestModel, TrailerTrackingV2CollectionModel } from "@store/Framework/Types/ApiModels";
import { Draft } from "immer";
import isEqual from "lodash/isEqual";

type ApiParameters = GetTrailerTrackingsV2ApiParameter;
type State = TrailerTrackingV2State;

interface FilterParameterGroup<ParameterType> {
    keys: Array<keyof ParameterType>;
    parentKey?: keyof ParameterType;
}

// Empty filterable parameters values
// Add values of filterable parameters when they should be considered inactive, all filterable parameters must be defined here.
const emptyFilterParameter: ApiParameters = {
    alertsOnly: false,
    billingType: "",
    customerSequence: "",
    dwellTimeDays: undefined,
    dwellTimeOperator: "",
    onSiteOnly: false,
    plantCode: "",
    poNumber: "",
    trailerId: "",
};

const defaultDwellTimeDays: ApiParameters["dwellTimeDays"] = 30;

// Default filterable parameters values
// Add default values of filterable parameters here if they should differ from their empty values and be MAINTAINED when reseting filters.
const defaultFilterParameter: ApiParameters = {
    ...emptyFilterParameter,
    onSiteOnly: true,
};

// Initial filterable parameters values
// Add initial values of filterable parameters here if they should differ from their empty values and should be EMPTIED when reseting filters.
const initialFilterParameter: ApiParameters = {
    ...defaultFilterParameter,
};

// Grouped filterable parameters
// For example: a date range with from/to parameters where both parameters should be populated to be considered as one applied filter.
const filterParameterGroups: FilterParameterGroup<ApiParameters>[] = [
    {
        parentKey: "dwellTimeOperator",
        keys: ["dwellTimeOperator", "dwellTimeDays"],
    },
];

// Initial parameters
// Add any default values for non-filterable parameters here if needed
const initialParameter: ApiParameters = {
    ...initialFilterParameter,
};

// Validate if a provided parameter value is considered empty
const isParameterEmpty = (parameter: ApiParameters, key: keyof ApiParameters) => {
    return (
        parameter[key] === undefined || parameter[key] === null || isEqual(parameter[key], emptyFilterParameter[key])
    );
};

// Cleanup the parameter object by removing entries that are considered empty
const cleanupParameter = (parameter: ApiParameters) => {
    const result = { ...parameter };

    filterParameterGroups.forEach(({ keys, parentKey }) => {
        if (parentKey) {
            const hasEmpty = keys.some(key => isParameterEmpty(result, key));

            if (hasEmpty) {
                keys.forEach(key => delete result[key]);
            }
        }
    });

    objectKeys(result)?.forEach(key => {
        if (isParameterEmpty(result, key)) {
            delete result[key];
        }
    });

    return result;
};

const cleanEmptyFilterParameter = cleanupParameter(emptyFilterParameter);
const cleanDefaultFilterParameter = cleanupParameter(defaultFilterParameter);

// Boolean that determines if the user has applied filters by themselves
const getHasFilters = (parameter: ApiParameters) => {
    return (
        objectKeys(defaultFilterParameter)?.some(key => !isEqual(parameter[key], cleanDefaultFilterParameter[key])) ??
        false
    );
};

// Returns cleared draft parameters
const getClearedDraftParameter = (draft: Draft<State>) => {
    return {
        ...draft.parameter, // maintain active non-filterable parameters
        ...defaultFilterParameter, // reset filterable parameters to their default values
        ...draft.defaultParameter, // apply default values from external sources
        dwellTimeDays: draft.draftParameter.dwellTimeDays || draft.parameter.dwellTimeDays || defaultDwellTimeDays,
    };
};

// Updates other state variables related to parameter changes
const updateParameterVariables = (draft: Draft<State>) => {
    // This function should be called in all actions that modify "parameter" or "draftParameter"
    const cleanDraftParameter = cleanupParameter(draft.draftParameter);
    const clearedDraftParameter = getClearedDraftParameter(draft);
    const cleanClearedDraftParameter = cleanupParameter(clearedDraftParameter);
    const countGroupStatus: boolean[] = [];

    draft.hasFilters = getHasFilters(draft.parameter);
    draft.canApplyFilters = false;
    draft.canClearFilters = false;
    draft.filterCount = 0;

    objectKeys(emptyFilterParameter)?.forEach(key => {
        if (!draft.canApplyFilters && !isEqual(draft.parameter[key], cleanDraftParameter[key])) {
            draft.canApplyFilters = true;
        }

        if (!draft.canClearFilters && !isEqual(cleanDraftParameter[key], cleanClearedDraftParameter[key])) {
            draft.canClearFilters = true;
        }

        if (!isEqual(draft.parameter[key], cleanEmptyFilterParameter[key])) {
            const hasGroup = filterParameterGroups.some(({ keys }, index) => {
                const isGrouped = Boolean(keys.find(groupKey => groupKey === key));

                if (isGrouped && !countGroupStatus[index]) {
                    countGroupStatus[index] = true;
                    draft.filterCount++;
                }

                return isGrouped;
            });

            if (!hasGroup) {
                draft.filterCount++;
            }
        }
    });

    filterParameterGroups.forEach(({ parentKey, keys }) => {
        if (parentKey && !isParameterEmpty(draft.draftParameter, parentKey)) {
            if (keys.some(key => key !== parentKey && isParameterEmpty(draft.draftParameter, key))) {
                draft.canApplyFilters = false;
            }
        }
    });

    return draft;
};

const initialState: State = updateParameterVariables({
    byId: {},
    canApplyFilters: false,
    canClearFilters: false,
    dataViews: {},
    defaultParameter: {},
    draftParameter: { ...initialParameter, dwellTimeDays: defaultDwellTimeDays },
    filterCount: 0,
    hasFilters: getHasFilters(cleanDefaultFilterParameter),
    isLoading: {},
    parameter: cleanupParameter(initialParameter),
    trailerPickupRequests: {},
});

const clearDraftParameter = (draft: Draft<State>) => {
    draft.draftParameter = getClearedDraftParameter(draft);

    // Reset to the first page whenever we are clearing active filters
    delete draft.draftParameter.page;
};

const updateDraftParameter = (draft: Draft<State>, action: { parameter: ApiParameters }) => {
    draft.draftParameter = { ...draft.draftParameter, ...action.parameter };

    // Reset to the first page whenever we change a filter except if its the page itself
    if (!action.parameter.page || action.parameter.page === 1) {
        delete draft.draftParameter.page;
    }
};

const applyDraftParameter = (draft: Draft<State>) => {
    draft.parameter = cleanupParameter(draft.draftParameter);
};

const actions = {
    "Greif/Pages/TrailerTrackingV2/BeginLoadTrailerTrackings": (
        draft: Draft<State>,
        action: { parameter: ApiParameters },
    ) => {
        setDataViewLoading(draft, action.parameter);
    },
    "Greif/Pages/TrailerTrackingV2/CompleteLoadTrailerTrackings": (
        draft: Draft<State>,
        action: { parameter: ApiParameters; collection: TrailerTrackingV2CollectionModel },
    ) => {
        const safeCollection = action.collection || {};
        const dataViewKey = getDataViewKey(action.parameter);

        setDataViewLoaded(
            draft,
            action.parameter,
            safeCollection,
            collection => {
                // We need to isolate each entry with a unique data view key because the trailer data within is also impacted by filters
                return (collection.trailerTrackings || []).map(x => ({
                    ...x,
                    id: `${x.id}_${dataViewKey}`,
                }));
            },
            undefined,
            (dataView: TrailerTrackingV2DataView) => {
                dataView.loadingFailed = !action.collection;
                dataView.plants = action.collection?.plants;
                dataView.filteredTrailerCount = action.collection?.filteredTrailerCount;
                dataView.totalTrailerCount = action.collection?.totalTrailerCount;
            },
        );
    },
    "Greif/Pages/TrailerTrackingV2/UpdateDraftParameter": (
        draft: Draft<State>,
        action: { parameter: ApiParameters },
    ) => {
        updateDraftParameter(draft, action);
        updateParameterVariables(draft);
    },
    "Greif/Pages/TrailerTrackingV2/ClearDraftParameter": (draft: Draft<State>) => {
        clearDraftParameter(draft);
        updateParameterVariables(draft);
    },
    "Greif/Pages/TrailerTrackingV2/ApplyDraftParameter": (draft: Draft<State>) => {
        applyDraftParameter(draft);
        updateParameterVariables(draft);
    },
    "Greif/Pages/TrailerTrackingV2/CancelDraftParameterChanges": (draft: Draft<State>) => {
        draft.draftParameter = {
            ...emptyFilterParameter,
            ...draft.parameter,
            dwellTimeDays: draft.parameter.dwellTimeDays || draft.draftParameter.dwellTimeDays || defaultDwellTimeDays,
        };

        updateParameterVariables(draft);
    },
    "Greif/Pages/TrailerTrackingV2/SetParameter": (draft: Draft<State>, action: { parameter: ApiParameters }) => {
        const parameter = { ...emptyFilterParameter, ...action.parameter };

        filterParameterGroups.forEach(({ keys, parentKey }) => {
            if (parentKey && keys.some(key => key !== parentKey && isParameterEmpty(parameter, key))) {
                delete parameter[parentKey];
            }
        });

        if (isParameterEmpty(parameter, "dwellTimeDays")) {
            parameter.dwellTimeDays = defaultDwellTimeDays;
        }

        updateDraftParameter(draft, { parameter });
        applyDraftParameter(draft);
        updateParameterVariables(draft);
    },
    "Greif/Pages/TrailerTrackingV2/ClearParameter": (draft: Draft<State>) => {
        clearDraftParameter(draft);
        applyDraftParameter(draft);
        updateParameterVariables(draft);
    },
    "Greif/Pages/TrailerTrackingV2/UpdateParameter": (draft: Draft<State>, action: { parameter: ApiParameters }) => {
        const parameter = { ...action.parameter };

        if (isParameterEmpty(parameter, "dwellTimeDays")) {
            parameter.dwellTimeDays = defaultDwellTimeDays;
        }

        updateDraftParameter(draft, { parameter });
        applyDraftParameter(draft);
        updateParameterVariables(draft);
    },
    "Greif/Pages/TrailerTrackingV2/UpdateDefaultParameter": (
        draft: Draft<State>,
        action: { parameter: ApiParameters },
    ) => {
        draft.defaultParameter = { ...draft.defaultParameter, ...action.parameter };
        updateParameterVariables(draft);
    },
    "Greif/Pages/TrailerTrackingV2/AddPickupRequest": (
        draft: Draft<State>,
        action: { trailerPickupRequest: TrailerPickupRequestModel },
    ) => {
        const key = getTrailerPickupRequestKey({
            shipToNumber: action.trailerPickupRequest.shipToNumber,
            trailerId: action.trailerPickupRequest.trailerId,
            shipmentNumber: action.trailerPickupRequest.shipmentNumber,
        });

        draft.trailerPickupRequests[key] = action.trailerPickupRequest;
    },
};

const reducer = createTypedReducerWithImmer(initialState, actions);
export type GreifTrailerTrackingV2PageActions = Parameters<typeof reducer>[1];
export default reducer;
